/**
 * Reading a whole PV plant out of an imported STL.
 *
 * The other path in this panel draws the array from the layout numbers and
 * shades it with the analytic row model. This one is for the case where the
 * model IS the plant: the STL already contains the modules, their mounting, the
 * buildings around them and the ground. Nothing may be drawn or computed twice,
 * so in this mode the generated rows go away entirely and every shadow - the
 * row in front included - comes out of the same ray cast.
 *
 * The one thing that has to be inferred is which triangles are modules. They
 * are the only large surfaces in a PV model that sit at exactly the layout's
 * tilt and bearing, so that is the test: the angle between a face normal and
 * the collector normal, against a tolerance. It is reported back with a count
 * and an area so a model that does not match can be seen not to match, rather
 * than silently measuring the roof of a shed.
 */

const DEG = Math.PI / 180;

/** Unit normal of a triangle, always pointing up. */
function faceNormal(m, t) {
  const ax = m[t + 3] - m[t], ay = m[t + 4] - m[t + 1], az = m[t + 5] - m[t + 2];
  const bx = m[t + 6] - m[t], by = m[t + 7] - m[t + 1], bz = m[t + 8] - m[t + 2];
  let nx = ay * bz - az * by;
  let ny = az * bx - ax * bz;
  let nz = ax * by - ay * bx;
  const len = Math.hypot(nx, ny, nz);
  if (!(len > 0)) return null;                       // degenerate: no plane
  nx /= len; ny /= len; nz /= len;
  // A module's front face points up. STL winding is not to be trusted, and a
  // flipped normal would put the face 180 degrees from the collector.
  if (nz < 0) { nx = -nx; ny = -ny; nz = -nz; }
  return { x: nx, y: ny, z: nz, area: len / 2 };
}

/** The collector normal for a tilt and a bearing, in the scene's axes. */
export function collectorNormal(tiltDeg, azimuthDeg) {
  const t = tiltDeg * DEG;
  const a = azimuthDeg * DEG;
  return {
    x: Math.sin(t) * Math.sin(a),                    // x is east
    y: Math.sin(t) * Math.cos(a),                    // y is north
    z: Math.cos(t),
  };
}

/**
 * Split an imported mesh into module faces and everything else.
 *
 * `tolDeg` is generous by default: a model drawn at 25 degrees and a layout
 * entered as 25 will match to the floating-point noise, but a model traced off
 * a survey may be a degree or two out, and refusing that is not helpful.
 */
export function classifyMesh(mesh, { tiltDeg, azimuthDeg, tolDeg = 8 } = {}) {
  const empty = {
    collectors: new Float32Array(0), others: new Float32Array(0),
    area: 0, count: 0, faces: 0, meanTilt: null, meanAzimuth: null,
  };
  if (!mesh || mesh.length < 9 || !Number.isFinite(tiltDeg) || !Number.isFinite(azimuthDeg)) {
    return empty;
  }
  const target = collectorNormal(tiltDeg, azimuthDeg);
  const limit = Math.cos(Math.max(0.5, tolDeg) * DEG);
  const keep = [];
  const rest = [];
  let area = 0;
  let sx = 0, sy = 0, sz = 0;
  for (let t = 0; t < mesh.length; t += 9) {
    const n = faceNormal(mesh, t);
    if (!n) continue;
    const cos = n.x * target.x + n.y * target.y + n.z * target.z;
    const dst = cos >= limit ? keep : rest;
    for (let k = 0; k < 9; k += 1) dst.push(mesh[t + k]);
    if (cos >= limit) {
      area += n.area;
      sx += n.x * n.area; sy += n.y * n.area; sz += n.z * n.area;
    }
  }
  const len = Math.hypot(sx, sy, sz);
  return {
    collectors: Float32Array.from(keep),
    others: Float32Array.from(rest),
    area,
    count: keep.length / 9,
    faces: mesh.length / 9,
    meanTilt: len > 0 ? Math.acos(Math.min(1, sz / len)) / DEG : null,
    meanAzimuth: len > 0 ? ((Math.atan2(sx / len, sy / len) / DEG) + 360) % 360 : null,
  };
}

/**
 * Points spread over the module faces, ready to be ray-cast.
 *
 * Deterministic on purpose. The stored shading table has to be reproducible:
 * the same model measured twice must give the same numbers, or a re-import
 * looks like a change in the site.
 *
 * Each point carries the row it belongs to, found by bucketing its position
 * along the across-rows axis at the layout pitch - which is what lets the panel
 * report "worst row" the same way it does for the generated array.
 */
export function sampleCollectors(collectors, { pitch, azimuthDeg, target = 1500 } = {}) {
  const out = [];
  if (!collectors || collectors.length < 9) return { samples: out, rows: 0 };

  let total = 0;
  const areas = [];
  const normals = [];
  for (let t = 0; t < collectors.length; t += 9) {
    const n = faceNormal(collectors, t);
    areas.push(n ? n.area : 0);
    normals.push(n);
    total += n ? n.area : 0;
  }
  if (!(total > 0)) return { samples: out, rows: 0 };
  const per = total / Math.max(1, target);

  const ax = Math.sin((azimuthDeg || 0) * DEG);
  const ay = Math.cos((azimuthDeg || 0) * DEG);
  const step = Number.isFinite(pitch) && pitch > 0.1 ? pitch : 1e9;
  const buckets = new Map();

  for (let i = 0, t = 0; t < collectors.length; t += 9, i += 1) {
    const n = normals[i];
    if (!n) continue;
    const k = Math.max(1, Math.round(areas[i] / per));
    for (let j = 0; j < k; j += 1) {
      // A deterministic low-discrepancy pair, mapped into the triangle. The
      // square-root map is what keeps the points uniform by area rather than
      // piled up at the first vertex.
      const r1 = (j + 0.5) / k;
      const r2 = ((j * 0.6180339887498949) % 1 + 1) % 1;
      const s = Math.sqrt(r1);
      const l1 = 1 - s;
      const l2 = s * (1 - r2);
      const l3 = s * r2;
      const x = l1 * collectors[t] + l2 * collectors[t + 3] + l3 * collectors[t + 6];
      const y = l1 * collectors[t + 1] + l2 * collectors[t + 4] + l3 * collectors[t + 7];
      const z = l1 * collectors[t + 2] + l2 * collectors[t + 5] + l3 * collectors[t + 8];
      // Lifted a millimetre off its own face: the depth test in blockedFraction
      // has a millimetre of slack, and a point sitting exactly in the plane of
      // the triangle it came from is the one case that slack exists for.
      const b = Math.round((x * ax + y * ay) / step);
      if (!buckets.has(b)) buckets.set(b, buckets.size);
      out.push({
        x: x + n.x * 0.001, y: y + n.y * 0.001, z: z + n.z * 0.001,
        nx: n.x, ny: n.y, nz: n.z,
        bucket: b,
      });
    }
  }

  // Row indices in order across the field, so "row 0" is one end and not
  // whichever triangle the file happened to list first.
  const order = [...buckets.keys()].sort((a, b) => a - b);
  const index = new Map(order.map((b, i) => [b, i]));
  out.forEach((p) => { p.row = index.get(p.bucket); });
  return { samples: out, rows: order.length };
}

/**
 * Everything the whole-plant mode needs from one imported model.
 *
 * The occluders are the WHOLE mesh, modules included: in this mode the row in
 * front is not a formula, it is geometry like everything else, and leaving the
 * modules out of the occluders would be the one omission that makes the answer
 * quietly optimistic.
 */
export function prepareWholePlant(mesh, { tiltDeg, azimuthDeg, pitch, tolDeg, target } = {}) {
  const parts = classifyMesh(mesh, { tiltDeg, azimuthDeg, tolDeg });
  const { samples, rows } = sampleCollectors(parts.collectors, {
    pitch, azimuthDeg, target,
  });
  return { ...parts, samples, rows, occluders: mesh };
}

/** A one-line verdict on whether the modules were actually found. */
export function describePlant(parts, { tiltDeg, azimuthDeg } = {}) {
  if (!parts || !parts.count) {
    return `no faces at ${Number(tiltDeg).toFixed(0)}° / ${Number(azimuthDeg).toFixed(0)}° found in the model`;
  }
  return (
    `${parts.count} module faces, ${parts.area.toFixed(0)} m² collector area` +
    (parts.meanTilt != null
      ? ` at ${parts.meanTilt.toFixed(1)}° / ${parts.meanAzimuth.toFixed(0)}°`
      : "")
  );
}

/**
 * The layout the model is actually built to.
 *
 * When the imported file contains the PV rows, the numbers typed into the
 * spacing panel are describing a plant that is already on screen - and if the
 * two disagree, the model is the one that was surveyed. This reads the layout
 * back out of it: how many rows, how far apart, how long, at what tilt and
 * bearing, and how much collector there is.
 *
 * The rows are found by their spacing rather than by any assumption about the
 * pitch. Projected onto the across-rows axis, a PV field's faces fall into
 * tight clusters separated by the pitch, so the gap distribution has an obvious
 * step in it: the largest ratio between consecutive sorted gaps is the boundary
 * between "same row" and "next row". Nothing about the layout is assumed to
 * find it, which is the point - the layout is what is being measured.
 */
export function measureLayout(collectors, { azimuthDeg = 180, moduleArea = null } = {}) {
  if (!collectors || collectors.length < 9) return null;
  const ax = Math.sin(azimuthDeg * DEG);
  const ay = Math.cos(azimuthDeg * DEG);
  const ux = Math.sin((azimuthDeg - 90) * DEG);
  const uy = Math.cos((azimuthDeg - 90) * DEG);

  const across = [];
  const along = [];
  let area = 0;
  for (let t = 0; t < collectors.length; t += 9) {
    const n = faceNormal(collectors, t);
    if (!n) continue;
    area += n.area;
    const cx = (collectors[t] + collectors[t + 3] + collectors[t + 6]) / 3;
    const cy = (collectors[t + 1] + collectors[t + 4] + collectors[t + 7]) / 3;
    across.push(cx * ax + cy * ay);
    for (let k = 0; k < 3; k += 1) {
      along.push(collectors[t + k * 3] * ux + collectors[t + k * 3 + 1] * uy);
    }
  }
  if (across.length === 0) return null;

  const sorted = [...across].sort((a, b) => a - b);
  const gaps = [];
  for (let i = 1; i < sorted.length; i += 1) {
    const g = sorted[i] - sorted[i - 1];
    if (g > 1e-6) gaps.push(g);
  }
  // The step in the gap distribution. With one row there is no step and no
  // pitch to report, which is a real answer rather than a division by nothing.
  let split = Infinity;
  if (gaps.length > 0) {
    const asc = [...gaps].sort((a, b) => a - b);
    let bestRatio = 0;
    for (let i = 1; i < asc.length; i += 1) {
      const ratio = asc[i] / Math.max(asc[i - 1], 1e-6);
      if (ratio > bestRatio && asc[i] > 0.25) {
        bestRatio = ratio;
        split = (asc[i] + asc[i - 1]) / 2;
      }
    }
    if (bestRatio < 3) split = Infinity;       // no clear structure: one row
  }

  const centres = [];
  let start = 0;
  for (let i = 1; i <= sorted.length; i += 1) {
    if (i === sorted.length || sorted[i] - sorted[i - 1] > split) {
      let sum = 0;
      for (let k = start; k < i; k += 1) sum += sorted[k];
      centres.push(sum / (i - start));
      start = i;
    }
  }

  const steps = [];
  for (let i = 1; i < centres.length; i += 1) steps.push(centres[i] - centres[i - 1]);
  steps.sort((a, b) => a - b);
  const pitch = steps.length ? steps[Math.floor(steps.length / 2)] : null;

  const rowLength = Math.max(...along) - Math.min(...along);
  const rows = centres.length;
  const parts = classifyMeshNormals(collectors);

  return {
    rows,
    pitch: pitch != null && pitch > 0 ? pitch : null,
    rowLength,
    area,
    // Ground the array stands on: how far the rows reach across, by how long
    // they are. Not the mesh's bounding box, which would include a shed.
    footprint: pitch != null && pitch > 0 ? rows * pitch * rowLength : null,
    tilt: parts.tilt,
    azimuth: parts.azimuth,
    modules: moduleArea > 0 ? Math.round(area / moduleArea) : null,
  };
}

/** Area-weighted mean orientation of a set of faces. */
function classifyMeshNormals(tris) {
  let sx = 0;
  let sy = 0;
  let sz = 0;
  for (let t = 0; t < tris.length; t += 9) {
    const n = faceNormal(tris, t);
    if (!n) continue;
    sx += n.x * n.area;
    sy += n.y * n.area;
    sz += n.z * n.area;
  }
  const len = Math.hypot(sx, sy, sz);
  if (!(len > 0)) return { tilt: null, azimuth: null };
  return {
    tilt: Math.acos(Math.min(1, sz / len)) / DEG,
    azimuth: ((Math.atan2(sx / len, sy / len) / DEG) + 360) % 360,
  };
}
